import React, { useEffect, useState } from "react";
import usePresence from "../../hooks/presence/usePresence";
import Loader from "../components/loader";
import axios from "axios";
import { ApiUrl, header } from "../../utils/modules";
import "../../assets/css/PresenceList.css";

function PresenceList() {
  const [presences, setPresences] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const { getAllPresence } = usePresence();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const presencesPerPage = 10;

  const today = new Date().toISOString().split("T")[0];
  const [dateJour, setDateJour] = useState(today);
  const [entrepriseId, setEntrepriseId] = useState("");
  const [entrepriseSelectionnee, setEntrepriseSelectionnee] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const isSuperviseur = user?.role === 2;

  // ── Fonction principale — reçoit tous les params directement, n'utilise PAS l'état ──
  const loadPresencesAvecParams = async (params = {}) => {
    try {
      setLoading(true);
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== "" && v != null),
      );
      const res = await getAllPresence(cleanParams);
      setPresences(res.data.data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Initialisation selon le rôle ──
  useEffect(() => {
    if (isSuperviseur && user?.entreprise) {
      const id = user.entreprise._id || user.entreprise;
      setEntrepriseId(id);

      axios
        .get(ApiUrl + "entreprise/" + id, header())
        .then((res) => {
          const ent = res.data.data;
          setEntrepriseSelectionnee(ent);
          loadPresencesAvecParams({
            entrepriseId: id,
            dateDebut: today,
            dateFin: today,
          });
        })
        .catch(console.error);
    } else {
      axios
        .get(ApiUrl + "entreprise", header())
        .then((res) => setEntreprises(res.data.data || []))
        .catch(console.error);

      loadPresencesAvecParams({ dateDebut: today, dateFin: today });
    }
  }, []);

  // ── Sync entrepriseSelectionnee quand le select change (admin) ──
  useEffect(() => {
    if (isSuperviseur) return;
    if (entrepriseId) {
      setEntrepriseSelectionnee(
        entreprises.find((e) => e._id === entrepriseId) || null,
      );
    } else {
      setEntrepriseSelectionnee(null);
    }
  }, [entrepriseId, entreprises]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadPresencesAvecParams({
      dateDebut: dateJour,
      dateFin: dateJour,
      entrepriseId,
    });
  };

  const handleReset = () => {
    setDateJour(today);
    if (!isSuperviseur) {
      setEntrepriseId("");
      setEntrepriseSelectionnee(null);
      loadPresencesAvecParams({ dateDebut: today, dateFin: today });
    } else {
      const id = user?.entreprise?._id || user?.entreprise;
      loadPresencesAvecParams({
        entrepriseId: id,
        dateDebut: today,
        dateFin: today,
      });
    }
  };

  const indexOfLast = currentPage * presencesPerPage;
  const indexOfFirst = indexOfLast - presencesPerPage;
  const currentPresences = presences.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(presences.length / presencesPerPage);

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    return new Date(dt).toLocaleTimeString("fr-MG", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Indian/Antananarivo",
    });
  };

  const initials = (nom = "", prenom = "") =>
    (nom[0] || "") + (prenom[0] || "");

  const formatSeconds = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
      pageNumbers.push(i);
    }
  }

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Présences</h2>
      </div>

      {/* FILTRES */}
      <div className="pres-filter-card">
        <div className="pres-filter-label-section">⚙ Filtres</div>
        <form onSubmit={handleSearch}>
          <div className="pres-filter-grid">
            <div className="pres-filter-field">
              <label>Date</label>
              <input
                type="date"
                value={dateJour}
                onChange={(e) => setDateJour(e.target.value)}
              />
            </div>
            <div className="pres-filter-field">
              <label>Entreprise</label>
              {isSuperviseur ? (
                <input
                  type="text"
                  value={entrepriseSelectionnee?.nom || "Chargement..."}
                  disabled
                  style={{ background: "#f5f5f5", cursor: "not-allowed" }}
                />
              ) : (
                <select
                  value={entrepriseId}
                  onChange={(e) => setEntrepriseId(e.target.value)}
                >
                  <option value="">— Toutes les entreprises —</option>
                  {entreprises.map((ent) => (
                    <option key={ent._id} value={ent._id}>
                      {ent.nom}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="pres-btn-group">
              <button type="submit" className="pres-btn-primary">
                🔍 Filtrer
              </button>
              {!isSuperviseur && (
                <button
                  type="button"
                  className="pres-btn-reset"
                  onClick={handleReset}
                >
                  ↺
                </button>
              )}
            </div>
          </div>
        </form>

        {entrepriseSelectionnee && (
          <div className="pres-company-banner">
            <span className="cname">🏢 {entrepriseSelectionnee.nom}</span>
            <div className="vsep" />
            <span className="hbadge">
              🕗 Ouverture :{" "}
              <strong>{entrepriseSelectionnee.heureOuverture}</strong>
            </span>
            <div className="vsep" />
            <span className="hbadge">
              🕔 Fermeture :{" "}
              <strong>{entrepriseSelectionnee.heureFermeture}</strong>
            </span>
          </div>
        )}
      </div>

      {/* TABLEAU */}
      <div className="pres-table-card">
        <div className="pres-table-head">
          <h2>Liste des présences</h2>
          <span className="pres-count-badge">
            {presences.length} résultat{presences.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading ? (
          <div
            style={{
              minHeight: 360,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loader />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Entreprise</th>
                    <th>Date</th>
                    <th>1ère activité</th>
                    <th>Dernière activité</th>
                    <th>Temps présence</th>
                    <th>% Présence</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPresences.length === 0 ? (
                    <tr className="pres-empty">
                      <td colSpan={7}>Aucune présence trouvée.</td>
                    </tr>
                  ) : (
                    currentPresences.map((presence) => {
                      const pct = Math.min(
                        100,
                        presence.pourcentagePresence || 0,
                      );
                      return (
                        <tr key={presence._id}>
                          <td>
                            <div className="pres-user-cell">
                              <div className="pres-avatar">
                                {initials(
                                  presence.utilisateur?.nom,
                                  presence.utilisateur?.prenom,
                                )}
                              </div>
                              <div>
                                <div className="pres-uname">
                                  {presence.utilisateur?.nom}{" "}
                                  {presence.utilisateur?.prenom}
                                </div>
                                <div className="pres-uemail">
                                  {presence.utilisateur?.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="pres-ent-tag">
                              {presence.utilisateur?.entreprise?.nom || "—"}
                            </span>
                          </td>
                          <td>
                            <span className="pres-date-chip">
                              📅 {presence.dateJour}
                            </span>
                          </td>
                          <td className="pres-time">
                            {formatDateTime(presence.premiereActivite)}
                          </td>
                          <td className="pres-time">
                            {formatDateTime(presence.derniereActivite)}
                          </td>
                          <td style={{ color: "#888", fontSize: 13 }}>
                            {formatSeconds(presence.tempsPresenceSecondes)}
                          </td>
                          <td>
                            <div className="pres-pct-wrap">
                              <div className="pres-pct-bg">
                                <div
                                  className="pres-pct-fill"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="pres-pct-label">
                                {(presence.pourcentagePresence || 0).toFixed(1)}
                                %
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pres-pagination">
                <span className="pinfo">
                  {indexOfFirst + 1}–{Math.min(indexOfLast, presences.length)}{" "}
                  sur {presences.length}
                </span>
                <div className="pres-page-btns">
                  <button
                    className="pres-page-btn"
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  {pageNumbers.map((n, i) => (
                    <React.Fragment key={n}>
                      {i > 0 && pageNumbers[i - 1] !== n - 1 && (
                        <span
                          style={{
                            fontSize: 12,
                            color: "#ccc",
                            padding: "0 2px",
                          }}
                        >
                          …
                        </span>
                      )}
                      <button
                        className={`pres-page-btn${currentPage === n ? " active" : ""}`}
                        onClick={() => setCurrentPage(n)}
                      >
                        {n}
                      </button>
                    </React.Fragment>
                  ))}
                  <button
                    className="pres-page-btn"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PresenceList;
